import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import {
  calcIsolatedMarginRemovable,
  calcPositionMarginExcludingFee,
} from '@flipster/features/src/shared/calculator/adjust-margin';
import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { tradeModeBunja } from '@flipster/features/src/trade-mode/state';
import { createPositionOrDismissAtom } from '@flipster/features/src/utils/position-or-dismiss';
import { i18nBunja } from '@flipster/i18n';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { validateBracketPositionSize } from '@flipster/utils/calculator/bracket-formula';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';

export type TabType = 'add' | 'remove';

export interface InputError {
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const AdjustMarginModalContext = createContext<{ slot: number; symbol: string } | null>(null);
export const AdjustMarginModalScope = createScopeFromContext(AdjustMarginModalContext);

export const adjustMarginStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, slot } = bunja.use(AdjustMarginModalScope) || {};
  const { getT } = bunja.use(i18nBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { positionBracketAtom, positionBracketMultiplierAtom, symbolLevelBracketParamsAtom } =
    bunja.use(positionBracketBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { tradeModeAtom } = bunja.use(tradeModeBunja);

  const t = getT();
  const amountAtom = atom<string>('');
  const tabAtom = atom<TabType>('add');

  const positionAtom = createPositionOrDismissAtom({ openPositionsAtom, symbol, slot });
  const currentPositionAtom = atom((get) => {
    const position = get(positionAtom);
    // Only available for isolated margin position
    if (position.marginType !== 'Isolated')
      throw new Error('Adjust margin is only available for isolated margin position');
    return position;
  });

  const instrumentAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[currentPosition.symbol];
    if (!instrument) throw new Error(`Instrument not found: ${currentPosition.symbol}`);
    return instrument;
  });

  const maxAmountAtom = atom<string>((get) => {
    const tab = get(tabAtom);
    const margin = get(getMarginAtom);
    const currentPosition = get(currentPositionAtom);
    const instrument = get(instrumentAtom);

    const { isolatedMarginAdded, isolatedMarginRealizedFee, unrealizedPnl } = currentPosition;
    const availableFunds = margin[instrument.quoteCurrency]?.isolatedMarginAvailable || '0';
    const maxAmount = calcIsolatedMarginRemovable(
      tab,
      availableFunds,
      isolatedMarginAdded,
      isolatedMarginRealizedFee,
      unrealizedPnl,
    );
    return maxAmount;
  });

  const dirtyAtom = atom({
    amount: false,
  });

  const bracketValidationAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    const tab = get(tabAtom);
    const positionBracket = get(positionBracketAtom);
    const tradeMode = get(tradeModeAtom);
    if (!symbol || !positionBracket || !tradeMode) return { isValid: false };
    if (tab !== 'remove' || currentPosition.marginType !== 'Isolated') return { isValid: true };

    const pswap = get(pswapsAtom)?.[symbol];

    if (!pswap) return { isValid: false };

    const pendingPositions = get(pendingPositionsAtom);

    // remove는 isolated margin만 지원하므로 entryPrice만 사용한다 (markPrice 불필요)
    const entryPrice = Num(currentPosition.avgEntryPrice || 0);

    const inputMargin = Num(get(amountAtom) || 0);
    const { existingPositionSize, existingMargin } = get(symbolLevelBracketParamsAtom);

    const currentPendingPositionSize = pendingPositions?.[symbol]?.reduce((acc, cur) => {
      if (cur.slot === currentPosition.slot) return acc.plus(Num(cur.leavesQty).multipliedBy(cur?.price || 0));
      return acc;
    }, Num(0));
    const currentPositionSize = Num(currentPosition.position)
      .abs()
      .multipliedBy(entryPrice)
      .plus(currentPendingPositionSize || 0);

    const currentMargin = Num(calcPositionMarginExcludingFee(currentPosition));
    const newMargin = Num(inputMargin).multipliedBy(-1);

    const positionBracketMultiplier = get(positionBracketMultiplierAtom);

    const { isValid } = validateBracketPositionSize({
      tradeMode,
      symbolLevelParams: {
        existingPositionSize,
        newPositionSize: Num(0),
        existingMargin,
        newLeverage: currentPosition.initLeverage,
      },
      positionLevelParams: {
        currentPositionSize,
        newPositionSize: Num(0),
        currentMargin,
        newMargin,
      },
      positionBracket,
      positionBracketMultiplier,
    });

    return { isValid };
  });

  const errorAtom = atom((get) => {
    const tab = get(tabAtom);
    const maxAmount = get(maxAmountAtom);
    const amount = get(amountAtom);
    const dirty = get(dirtyAtom);
    const instrument = get(instrumentAtom);
    if (!dirty.amount) return undefined;
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

    switch (tab) {
      case 'add': {
        if (Num(amount).gt(maxAmount)) {
          return {
            message: t('Position_add_funds.r250828.Add_error_max_exceeded', {
              amount: numberFormatter(maxAmount, { precision: quoteCurrencyPrecision }),
              quoteCurrency: instrument.quoteCurrency,
            }),
          };
        }
        break;
      }

      case 'remove': {
        if (Num(amount).gt(maxAmount)) {
          return {
            message: t('Position_add_funds.r250828.Remove_error_max_exceeded', {
              amount: numberFormatter(maxAmount, { precision: quoteCurrencyPrecision }),
              quoteCurrency: instrument.quoteCurrency,
            }),
          };
        }

        // adjust margin에서 position bracket은 remove인 경우에만 검증한다.
        const { isValid } = get(bracketValidationAtom);
        if (!isValid) {
          return {
            message: t('Trade.r260107.Decrease_effective_leverage'),
            action: {
              label: t('Common.r250401.Learn_more'),
              onClick: () => {
                window.open(`/support/trading-rules/leverage-margin/${symbol}`, '_blank');
              },
            },
          };
        }
        break;
      }
    }

    return undefined;
  });

  const isValidAtom = atom((get) => {
    const error = get(errorAtom);
    const dirty = get(dirtyAtom);
    const amount = get(amountAtom);
    return dirty.amount && !Num(amount).isNaN() && !error;
  });

  const resetForm = () => {
    store.set(amountAtom, '');
    store.set(dirtyAtom, { amount: false });
  };

  const setAmount = (value: string) => {
    store.set(amountAtom, value);
    store.set(dirtyAtom, (prev) => ({ ...prev, amount: true }));
  };

  return {
    amountAtom,
    tabAtom,

    currentPositionAtom,
    maxAmountAtom,
    instrumentAtom,
    dirtyAtom,
    resetForm,
    setAmount,
    errorAtom,
    isValidAtom,

    symbol: symbol!,
    slot: slot!,
  };
});
adjustMarginStateBunja.debugLabel = 'adjustMarginStateBunja';
